import { memo, useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { Ban, Search, X } from 'lucide-react';
import { cn } from '@/lib/utils';

// THE LIBRARY PICKER (owner 2026-10-20: "add a entire library of overlays that users can choose from ... it should
// be enough that i never have a complaint from a user about not being able to find what they want"; 2026-10-21: "look
// at snapchat, tiktok, insta, the leading media editing suites, etc. take what their best features are and combine
// them"; then "the overlays and backdrops are wayyy too small to even see clearly ... make the overlay category
// selections off to the side"). What it takes from each:
//   Instagram  a backdrop tile can show your cut-out in front of it; an overlay tile shows the overlay alone on a plain
//              tone (owner 2026-11-01: the main image under each one "makes it harder to see what is in the thumbnails");
//   CapCut     the categories down the side, big tiles beside them;
//   TikTok     clean tiles with no text stamped on them, a search bar, "None" first;
//   Snapchat   the picked one gets a bold ring and its name; tap it again to take it off (the corner x says so).
// Tiles are stills, so the tray scrolls smoothly; on a computer a tile plays while the mouse is on it.

export interface PickItem {
  id: string;
  label: string;
  group: string;
  /** The tile's still (a frame of the clip, or of the engine's own drawing). */
  thumb?: string;
  /** A small looping clip, played only while a mouse is on the tile. */
  preview?: string;
  /** An overlay's blend ('screen', 'overlay', 'multiply', 'normal'): its tile lays it on the photo that way. */
  blend?: string;
  /** Extra words the search matches (the other language's name, the kind). */
  words?: string;
}

export interface PickGroup {
  id: string;
  label: string;
}

/** A tile's name without the library's running number ("City lights at night 4"): the still tells them apart. */
const plain = (label: string) => label.replace(/\s+\d+$/, '');

// What an overlay is laid on (light on a night tone, a texture on a mid tone).
const UNDER: Record<string, string> = {
  screen: 'radial-gradient(ellipse at 30% 70%, #2b3a55 0%, #161d2b 50%, #0b0f17 100%)',
  overlay: 'radial-gradient(ellipse at 30% 70%, #7d8aa3 0%, #4b566b 55%, #2a3140 100%)',
  multiply: 'radial-gradient(ellipse at 30% 70%, #c9cfdb 0%, #9aa3b5 55%, #6b7486 100%)',
  normal: 'radial-gradient(ellipse at 30% 70%, #2b3a55 0%, #161d2b 50%, #0b0f17 100%)',
};
const BLEND_MODE: Record<string, CSSProperties['mixBlendMode']> = { screen: 'screen', overlay: 'overlay', multiply: 'multiply', normal: 'normal' };

const hoverable = typeof window !== 'undefined' && Boolean(window.matchMedia?.('(hover: hover) and (pointer: fine)').matches);

/** What a backdrop tile can show in front of it: the customer's cut-out. */
export interface TileBase {
  photo?: string | null;
  /** A backdrop tile: the cut-out's mask (white with alpha, 9:16), the photo shown only there, in front. */
  cutMask?: string | null;
}

/**
 * A tile's picture: a 9:16 frame (the cover's own shape, so the cut-out's mask lines up with the photo) centred in a
 * wider 3:4 tile, so the middle of the picture, where the person is, fills a bigger box.
 */
function Frame({ children }: { children: ReactNode }) {
  return <span className="absolute left-0 top-1/2 block aspect-[9/16] w-full -translate-y-1/2">{children}</span>;
}

const Tile = memo(function Tile({ item, on, onPick, base, backdrop, offLabel }: { item: PickItem; on: boolean; onPick: (id: string) => void; base: TileBase; backdrop: boolean; offLabel: string }) {
  const [hover, setHover] = useState(false);
  const mode = item.blend ? BLEND_MODE[item.blend] : undefined;
  return (
    <button
      type="button"
      aria-pressed={on}
      aria-label={on ? `${plain(item.label)} · ${offLabel}` : item.label}
      title={plain(item.label)}
      onClick={() => onPick(item.id)}
      onPointerEnter={hoverable && item.preview ? () => setHover(true) : undefined}
      onPointerLeave={hoverable && item.preview ? () => setHover(false) : undefined}
      data-kind={item.id}
      className="group relative block w-full text-left outline-none"
    >
      <span
        className={cn('relative block aspect-[3/4] w-full overflow-hidden rounded-2xl bg-[#11141b] transition-[transform,box-shadow] duration-150 [contain:content] group-active:scale-[0.96] group-focus-visible:ring-2 group-focus-visible:ring-primary',
          on ? 'shadow-[0_0_0_3px_hsl(var(--primary)),0_8px_22px_-8px_hsl(var(--primary)/0.7)]' : 'shadow-[0_0_0_1px_rgba(255,255,255,0.06)] group-hover:shadow-[0_0_0_2px_rgba(255,255,255,0.35)]')}
      >
        <Frame>
          {/* Overlays: on a plain tone, laid on the way the cover lays them. */}
          {!backdrop && <span aria-hidden className="absolute inset-0" style={{ background: UNDER[item.blend || 'screen'] || UNDER.screen }} />}
          {item.thumb && <img src={item.thumb} alt="" loading="lazy" decoding="async" draggable={false} className="absolute inset-0 h-full w-full object-cover" style={{ mixBlendMode: backdrop ? undefined : mode }} />}
          {hover && item.preview && <video src={item.preview} autoPlay muted loop playsInline aria-hidden className="absolute inset-0 h-full w-full object-cover" style={{ mixBlendMode: backdrop ? undefined : mode }} />}
          {backdrop && base.photo && base.cutMask && (
            <img
              src={base.photo}
              alt=""
              aria-hidden
              draggable={false}
              className="absolute inset-0 h-full w-full object-cover"
              style={{ maskImage: `url("${base.cutMask}")`, WebkitMaskImage: `url("${base.cutMask}")`, maskSize: '100% 100%', WebkitMaskSize: '100% 100%' }}
            />
          )}
        </Frame>
        {/* Picked: a corner x, so it's plain that tapping again takes it off. */}
        {on && (
          <span aria-hidden className="absolute right-1.5 top-1.5 flex h-6 w-6 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-md">
            <X className="h-4 w-4" strokeWidth={3} />
          </span>
        )}
      </span>
      <span className={cn('mt-1 block truncate text-center text-[12px] font-semibold leading-tight', on ? 'text-primary' : 'text-foreground/60')} aria-hidden>{plain(item.label)}</span>
    </button>
  );
});

function NoneTile({ on, label, onNone }: { on: boolean; label: string; onNone: () => void }) {
  return (
    <button type="button" aria-pressed={on} onClick={onNone} data-kind="none" className="group relative block w-full outline-none">
      <span
        className={cn('flex aspect-[3/4] w-full flex-col items-center justify-center gap-1.5 rounded-2xl bg-white/[0.05] text-foreground/75 transition-transform group-active:scale-[0.96]',
          on ? 'shadow-[0_0_0_3px_hsl(var(--primary))]' : 'shadow-[0_0_0_1px_rgba(255,255,255,0.06)] group-hover:shadow-[0_0_0_2px_rgba(255,255,255,0.35)]')}
      >
        <Ban className="h-7 w-7" aria-hidden />
      </span>
      <span className={cn('mt-1 block text-center text-[12px] font-semibold leading-tight', on ? 'text-primary' : 'text-foreground/60')}>{label}</span>
    </button>
  );
}

export function LibraryPicker({
  items,
  groups,
  picked,
  max,
  onToggle,
  onNone,
  noneLabel,
  searchLabel,
  noMatch,
  fullLabel,
  allLabel,
  clearLabel,
  offLabel,
  base = {},
  backdrop = false,
  single = false,
  fullBar,
  testid,
}: {
  items: PickItem[];
  groups: PickGroup[];
  picked: string[];
  max: number;
  /** A tap: picks it, or takes it off when it is already picked. */
  onToggle: (id: string) => void;
  /** The "None" tile, first: clears the pick(s). */
  onNone: () => void;
  noneLabel: string;
  searchLabel: string;
  noMatch: string;
  /** Said when one more is tapped past `max` (several at once only). */
  fullLabel: string;
  allLabel: string;
  clearLabel: string;
  /** "tap to take off", read out on a picked tile. */
  offLabel: string;
  base?: TileBase;
  /** Backdrop tiles: the item is the background, the customer's cut-out in front. */
  backdrop?: boolean;
  /** One at a time: picking another replaces it. */
  single?: boolean;
  /**
   * At the limit (owner 2026-10-23: "if they max out while looking they should be able to erase one without needing to
   * go back to the original to deselect"): a bar pinned over the tiles with each pick and its own x. `title` gets the
   * count ("3 of 3 picked"), `remove` the pick's name for the x's label.
   */
  fullBar?: { title: (n: number, max: number) => string; remove: (name: string) => string };
  testid: string;
}) {
  const shownGroups = useMemo(() => groups.filter((g) => items.some((i) => i.group === g.id)), [groups, items]);
  const [group, setGroup] = useState<string>('all');
  const [query, setQuery] = useState('');
  const [full, setFull] = useState(false);
  const fullTimer = useRef<number | undefined>();
  const grid = useRef<HTMLDivElement>(null);
  // The group list's own height: what is visible of the panel below its top, kept as the panel scrolls and resizes,
  // so its last groups are always reachable by its own scroll (owner 2026-10-24: "you cant scroll them because they
  // dont have their own scroll bar so you have to wait until the one on the right catches up").
  const groupsRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const col = groupsRef.current;
    if (!col) return undefined;
    let box: HTMLElement | null = col.parentElement;
    while (box && !/(auto|scroll)/.test(getComputedStyle(box).overflowY)) box = box.parentElement;
    const fit = () => {
      const bottom = box ? box.getBoundingClientRect().bottom : window.innerHeight;
      col.style.maxHeight = `${Math.max(160, Math.round(Math.min(bottom, window.innerHeight) - col.getBoundingClientRect().top - 8))}px`;
    };
    fit();
    const target: HTMLElement | Window = box || window;
    target.addEventListener('scroll', fit, { passive: true });
    window.addEventListener('resize', fit);
    return () => {
      target.removeEventListener('scroll', fit);
      window.removeEventListener('resize', fit);
    };
  }, []);
  const q = query.trim().toLowerCase();
  const groupName = useMemo(() => new Map(groups.map((g) => [g.id, g.label])), [groups]);
  const shown = useMemo(() => {
    if (q) return items.filter((i) => `${i.label} ${i.words || ''} ${groupName.get(i.group) || ''}`.toLowerCase().includes(q));
    return group === 'all' ? items : items.filter((i) => i.group === group);
  }, [items, q, group, groupName]);
  const pick = (id: string) => {
    if (!single && !picked.includes(id) && picked.length >= max) {
      setFull(true);
      window.clearTimeout(fullTimer.current);
      fullTimer.current = window.setTimeout(() => setFull(false), 3200);
      return;
    }
    setFull(false);
    onToggle(id);
  };
  const open = (id: string) => {
    setGroup(id);
    setQuery('');
    grid.current?.scrollIntoView?.({ block: 'nearest' });
  };
  const rail = [{ id: 'all', label: allLabel }, ...shownGroups];
  return (
    <div className="min-w-0" data-testid={testid}>
      <label className="relative block">
        <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={searchLabel}
          aria-label={searchLabel}
          className="h-11 w-full rounded-full border-0 bg-white/[0.06] pl-10 pr-10 text-[16px] text-foreground placeholder:text-muted-foreground focus:bg-white/[0.09] focus:outline-none focus:ring-2 focus:ring-primary/60"
        />
        {query && (
          <button type="button" onClick={() => setQuery('')} aria-label={clearLabel} className="absolute right-2 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-foreground/80">
            <X className="h-3.5 w-3.5" aria-hidden />
          </button>
        )}
      </label>
      {full && !fullBar && (
        <p role="status" className="mt-2 rounded-xl bg-primary/15 px-3 py-2 text-[13px] font-medium text-foreground" data-testid={`${testid}-full`}>{fullLabel}</p>
      )}
      {fullBar && !single && picked.length >= max && (
        <div role="status" className={cn('sticky top-0 z-10 mt-2 rounded-xl px-3 py-2 shadow-[0_8px_20px_-12px_rgba(0,0,0,0.9)] backdrop-blur transition-colors', full ? 'bg-primary/30 ring-2 ring-primary' : 'bg-[#1b1f28]/95 ring-1 ring-white/10')} data-testid={`${testid}-full`}>
          <p className="text-[13px] font-semibold text-foreground">{fullBar.title(picked.length, max)}</p>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {picked.map((id) => {
              const name = plain(items.find((i) => i.id === id)?.label || id);
              return (
                <button key={id} type="button" onClick={() => { setFull(false); onToggle(id); }} aria-label={fullBar.remove(name)} data-testid={`${testid}-remove-${id}`}
                  className="inline-flex min-h-[34px] items-center gap-1 rounded-full bg-white/[0.1] pl-3 pr-2 text-[13px] font-semibold text-foreground hover:bg-white/[0.16]">
                  {name}<X className="h-3.5 w-3.5 text-foreground/80" aria-hidden />
                </button>
              );
            })}
          </div>
        </div>
      )}
      {/* The categories down the side (CapCut), pinned while the big tiles beside them scroll with the panel (one
          scroll, no box scrolling inside another). */}
      <div ref={grid} className="mt-3 flex scroll-mt-2 items-start gap-2">
        {/* Its own scroll, always inside the panel, with a thin bar you can see (owner 2026-10-24: "you cant scroll them
            because they dont have their own scroll bar so you have to wait until the one on the right catches up"). */}
        <div ref={groupsRef} className="sticky top-0 flex max-h-[min(34rem,calc(46dvh-5.5rem))] w-[5.75rem] shrink-0 flex-col gap-1 overflow-y-auto overscroll-contain pr-1 [scrollbar-color:rgba(255,255,255,0.35)_transparent] [scrollbar-width:thin] [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-white/35 [&::-webkit-scrollbar]:w-1.5" role="tablist" aria-orientation="vertical" data-testid={`${testid}-groups`}>
          {rail.map((g) => {
            const on = !q && group === g.id;
            return (
              <button
                key={g.id}
                type="button"
                role="tab"
                aria-selected={on}
                onClick={() => open(g.id)}
                className={cn('relative min-h-[40px] shrink-0 rounded-xl px-2.5 py-2 text-left text-[13px] font-semibold leading-tight transition-colors',
                  on ? 'bg-white/[0.1] text-foreground' : 'text-foreground/60 hover:bg-white/[0.05] hover:text-foreground')}
              >
                {on && <span aria-hidden className="absolute inset-y-2 left-0 w-[3px] rounded-full bg-primary" />}
                {g.label}
              </button>
            );
          })}
        </div>
        <div className="min-w-0 flex-1">
          {shown.length === 0 ? (
            <p className="rounded-xl bg-white/[0.04] px-3 py-6 text-center text-[13.5px] text-muted-foreground">{noMatch}</p>
          ) : (
            <div className="grid grid-cols-2 gap-x-2.5 gap-y-2 p-1 sm:grid-cols-3 lg:grid-cols-2 xl:grid-cols-3">
              {!q && <NoneTile on={picked.length === 0} label={noneLabel} onNone={onNone} />}
              {shown.map((i) => <Tile key={i.id} item={i} on={picked.includes(i.id)} onPick={pick} base={base} backdrop={backdrop} offLabel={offLabel} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
